/**
 * widgetContentMap.ts
 *
 * Maps templateKey and chartType to render components, replacing the 40-branch
 * if/switch chain in WidgetWrapper.renderContent().
 *
 * Three maps are exported:
 *   TEMPLATE_RENDERERS   — keyed by templateKey (checked first)
 *   LEARN_METRIC_RENDERERS — keyed by resolved metricKey (checked second, learn_ only)
 *   CHART_TYPE_RENDERERS — keyed by chartType (fallback)
 */

import React from 'react';
import type { ComponentType } from 'react';

// ── Design system component imports ──────────────────────────────────────────

import { ProfileCard } from '@t-one-internal/design-system/lib/components/widgets/my-portal/ProfileCard';
import { StatusSummary } from '@t-one-internal/design-system/lib/components/widgets/my-portal/StatusSummary';
import { RankedList } from '@t-one-internal/design-system/lib/components/widgets/my-portal/RankedList';
import { ActionList } from '@t-one-internal/design-system/lib/components/widgets/my-portal/ActionList';
import { MultiKPI } from '@t-one-internal/design-system/lib/components/widgets/my-portal/MultiKPI';
import { GoalList } from '@t-one-internal/design-system/lib/components/widgets/my-portal/GoalList';
import { CardList } from '@t-one-internal/design-system/lib/components/widgets/my-portal/CardList';
import { ReviewCycleCard } from '@t-one-internal/design-system/lib/components/widgets/my-portal/ReviewCycleCard';
import { CheckInCycleCard } from '@t-one-internal/design-system/lib/components/widgets/my-portal/CheckInCycleCard';
import { FeedbackCard } from '@t-one-internal/design-system/lib/components/widgets/my-portal/FeedbackCard';
import { PendingTasksCard } from '@t-one-internal/design-system/lib/components/widgets/my-portal/PendingTasksCard';
import { LearningPlanCard } from '@t-one-internal/design-system/lib/components/widgets/my-portal/LearningPlanCard';
import { RatingComparisonList } from '@t-one-internal/design-system/lib/components/widgets/my-portal/RatingComparisonList';
import { CycleCard } from '@t-one-internal/design-system/lib/components/widgets/my-portal/CycleCard';
import { AtRiskCard } from '@t-one-internal/design-system/lib/components/widgets/my-portal/AtRiskCard';
import { GoalCard } from '@t-one-internal/design-system/lib/components/widgets/my-portal/GoalCard';
import { EmployeeCard } from '@t-one-internal/design-system/lib/components/widgets/my-portal/EmployeeCard';
import { StaleGoalCard } from '@t-one-internal/design-system/lib/components/widgets/my-portal/StaleGoalCard';
import { AlertCards } from '@t-one-internal/design-system/lib/components/widgets/my-portal/AlertCards';

// ── Internal widget imports ───────────────────────────────────────────────────

import { GoalsProgressWidget } from './GoalsProgressWidget';
import { PIPsWidget } from './PIPsWidget';
import { IDPsWidget } from './IDPsWidget';
import { PerformanceSummaryWidget } from './PerformanceSummaryWidget';
import { FeedbacksReceivedWidget } from './FeedbacksReceivedWidget';

// ── Learn widget imports ──────────────────────────────────────────────────────

import {
  MyActiveCoursesWidget,
  ContinueLearningWidget,
  UpcomingDeadlinesWidget,
  CertificatesEarnedWidget,
  MyLearningActivityWidget,
  SkillDevelopmentWidget,
  MyUpcomingSessionsWidget,
  MyCoachingJourneyWidget,
  TeamCourseCompletionWidget,
  TeamOverdueCoursesWidget,
  TeamEventAttendanceWidget,
  TeamCoachingSnapshotWidget,
  OrgKPISummaryWidget,
  OrgChannelMixWidget,
  OrgTopCoursesWidget,
  OrgCompletionByCategoryWidget,
  OrgTopLearnersWidget,
  OrgILTUtilizationWidget,
  OrgCertificateIssuanceWidget,
  CalendarCardWidget,
  CoachingGoalCompletionWidget,
  CoachingAtRiskEngagementsWidget,
  CoachingCompetencyOverviewWidget,
  MyCoachingSnapshotWidget,
  MyCoachingGoalProgressWidget,
  CoachCoacheeSummaryWidget,
  CoachSessionStatsWidget,
  CustomerCoachingOverviewWidget,
} from './learn/index';

// ─── Shared props interface ───────────────────────────────────────────────────

/**
 * The common props contract that every renderer in the maps below must accept.
 * Wrapper components can destructure what they need and ignore the rest.
 */
export interface WidgetContentProps {
  data: any;
  loading?: boolean;
  error?: string | null;
  templateKey?: string;
  chartType?: string;
  visualizationConfig?: any;
  queryConfig?: any;
  resolvedMetricKey?: string;
  onPointClick?: (point: any) => void;
}

// ─── TEMPLATE_RENDERERS ───────────────────────────────────────────────────────
// Keyed by templateKey. Components are thin wrappers that adapt WidgetContentProps
// to the underlying design-system or internal component props.

export const TEMPLATE_RENDERERS: Record<string, ComponentType<WidgetContentProps>> = {

  // ── My Portal — Core Backend ──────────────────────────────────────────────
  mp_user_card: ({ data, visualizationConfig }: WidgetContentProps) => (
    // coreData is passed as `data` by WidgetWrapper when dataSource === 'core_backend'
    // eslint-disable-next-line react/jsx-pascal-case
    <ProfileCard data={data} options={visualizationConfig?.options} />
  ),

  // ── My Portal — Review Cloud ──────────────────────────────────────────────
  mp_actions: ({ data, visualizationConfig }: WidgetContentProps) => (
    <PendingTasksCard data={data} options={visualizationConfig?.options} />
  ),

  // ── My Portal — Learn Backend ─────────────────────────────────────────────
  mp_learning_plan: ({ data, visualizationConfig }: WidgetContentProps) => (
    <LearningPlanCard data={data} options={visualizationConfig?.options} />
  ),

  // ── My Portal — Analytics ─────────────────────────────────────────────────
  mp_review_cycles_history: ({ data, visualizationConfig }: WidgetContentProps) => (
    <ReviewCycleCard data={data} options={visualizationConfig?.options} />
  ),

  mp_checkin_summary: ({ data, visualizationConfig }: WidgetContentProps) => (
    <CheckInCycleCard data={data} options={visualizationConfig?.options} />
  ),

  mp_360_feedback_as_reviewer: ({ data, visualizationConfig }: WidgetContentProps) => (
    <FeedbackCard data={data} options={visualizationConfig?.options} variant="reviewer" />
  ),

  mp_360_feedback_as_reviewee: ({ data, visualizationConfig }: WidgetContentProps) => (
    <FeedbackCard data={data} options={visualizationConfig?.options} variant="reviewee" />
  ),

  mp_goals_progress: ({ data, loading, error, visualizationConfig }: WidgetContentProps) => (
    <GoalsProgressWidget
      goals={data}
      loading={loading}
      error={error ?? undefined}
      options={visualizationConfig?.options}
    />
  ),

  mp_goals_progress_list: ({ data, loading, error, visualizationConfig }: WidgetContentProps) => (
    <GoalsProgressWidget
      goals={data}
      loading={loading}
      error={error ?? undefined}
      options={visualizationConfig?.options}
    />
  ),

  mp_pips: ({ data, loading, error, visualizationConfig }: WidgetContentProps) => (
    <PIPsWidget
      pips={data}
      loading={loading}
      error={error ?? undefined}
      options={visualizationConfig?.options}
    />
  ),

  mp_idps: ({ data, loading, error, visualizationConfig }: WidgetContentProps) => (
    <IDPsWidget
      idps={data}
      loading={loading}
      error={error ?? undefined}
      options={visualizationConfig?.options}
    />
  ),

  mp_performance_summary: ({ data, loading, error, visualizationConfig }: WidgetContentProps) => (
    <PerformanceSummaryWidget
      data={data}
      loading={loading}
      error={error ?? undefined}
      options={visualizationConfig?.options}
    />
  ),

  mp_feedbacks_received: ({ data, loading, error, visualizationConfig }: WidgetContentProps) => (
    <FeedbacksReceivedWidget
      feedbacks={data}
      loading={loading}
      error={error ?? undefined}
      options={visualizationConfig?.options}
    />
  ),

  // ── Cycle / Goalboard card views ──────────────────────────────────────────
  cycles_list: ({ data, visualizationConfig }: WidgetContentProps) => (
    <CycleCard data={data} options={visualizationConfig?.options} />
  ),

  cycles_at_risk_list: ({ data, visualizationConfig }: WidgetContentProps) => (
    <AtRiskCard data={data} options={visualizationConfig?.options} />
  ),

  goalboard_goals_list: ({ data, visualizationConfig }: WidgetContentProps) => (
    <GoalCard data={data} options={visualizationConfig?.options} />
  ),

  goalboard_employees_without_goals: ({ data, visualizationConfig }: WidgetContentProps) => (
    <EmployeeCard data={data} options={visualizationConfig?.options} />
  ),

  goalboard_stale_goals: ({ data, visualizationConfig }: WidgetContentProps) => (
    <StaleGoalCard data={data} options={visualizationConfig?.options} />
  ),
};

// ─── LEARN_METRIC_RENDERERS ───────────────────────────────────────────────────
// Keyed by resolved metricKey. All learn widgets accept {data, loading, error}.

export const LEARN_METRIC_RENDERERS: Record<string, ComponentType<WidgetContentProps>> = {
  // My portal — individual learner widgets
  learn_my_active_courses: ({ data, loading, error }: WidgetContentProps) => (
    <MyActiveCoursesWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  // continue_learning and upcoming_deadlines share the same data shape as active courses
  learn_continue_learning: ({ data, loading, error }: WidgetContentProps) => (
    <ContinueLearningWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  learn_upcoming_deadlines: ({ data, loading, error }: WidgetContentProps) => (
    <UpcomingDeadlinesWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  learn_my_certificates: ({ data, loading, error }: WidgetContentProps) => (
    <CertificatesEarnedWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  learn_my_activity_summary: ({ data, loading, error }: WidgetContentProps) => (
    <MyLearningActivityWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  learn_my_skill_development: ({ data, loading, error }: WidgetContentProps) => (
    <SkillDevelopmentWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  learn_my_upcoming_sessions: ({ data, loading, error }: WidgetContentProps) => (
    <MyUpcomingSessionsWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  learn_my_coaching: ({ data, loading, error }: WidgetContentProps) => (
    <MyCoachingJourneyWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  learn_my_coaching_goals: ({ data, loading, error }: WidgetContentProps) => (
    <MyCoachingJourneyWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  // Team widgets
  learn_team_course_completion: ({ data, loading, error }: WidgetContentProps) => (
    <TeamCourseCompletionWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  // Alias: backend sends learn_team_completion, renderer expects learn_team_course_completion
  learn_team_completion: ({ data, loading, error }: WidgetContentProps) => (
    <TeamCourseCompletionWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  learn_team_overdue_courses: ({ data, loading, error }: WidgetContentProps) => (
    <TeamOverdueCoursesWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  // Alias: backend sends learn_team_overdue, renderer expects learn_team_overdue_courses
  learn_team_overdue: ({ data, loading, error }: WidgetContentProps) => (
    <TeamOverdueCoursesWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  learn_team_event_attendance: ({ data, loading, error }: WidgetContentProps) => (
    <TeamEventAttendanceWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  learn_team_coaching_snapshot: ({ data, loading, error }: WidgetContentProps) => (
    <TeamCoachingSnapshotWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  // Org widgets
  // NOTE: learn_org_kpi_summary is NOT registered here. Multiple KPI card
  // widgets share this metric and the inline switch(templateKey) in
  // WidgetWrapper correctly picks which field to show per widget.

  learn_org_channel_mix: ({ data, loading, error }: WidgetContentProps) => (
    <OrgChannelMixWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  learn_org_top_courses: ({ data, loading, error }: WidgetContentProps) => (
    <OrgTopCoursesWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  learn_org_completion_by_category: ({ data, loading, error }: WidgetContentProps) => (
    <OrgCompletionByCategoryWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  learn_org_top_learners: ({ data, loading, error }: WidgetContentProps) => (
    <OrgTopLearnersWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  learn_org_ilt_utilization: ({ data, loading, error }: WidgetContentProps) => (
    <OrgILTUtilizationWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  learn_org_certificate_issuance: ({ data, loading, error }: WidgetContentProps) => (
    <OrgCertificateIssuanceWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  // Coaching — Learn Admin
  learn_coaching_goal_completion_rate: ({ data, loading, error }: WidgetContentProps) => (
    <CoachingGoalCompletionWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  learn_coaching_at_risk_engagements: ({ data, loading, error }: WidgetContentProps) => (
    <CoachingAtRiskEngagementsWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  learn_coaching_assessment_heatmap: ({ data, loading, error }: WidgetContentProps) => (
    <CoachingCompetencyOverviewWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  // Coaching — Coachee (self)
  learn_my_coaching_snapshot: ({ data, loading, error }: WidgetContentProps) => (
    <MyCoachingSnapshotWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  learn_my_goal_progress: ({ data, loading, error }: WidgetContentProps) => (
    <MyCoachingGoalProgressWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  // Coaching — Coach (self)
  learn_coach_coachee_summary: ({ data, loading, error }: WidgetContentProps) => (
    <CoachCoacheeSummaryWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  learn_coach_session_stats: ({ data, loading, error }: WidgetContentProps) => (
    <CoachSessionStatsWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  // Coaching — Customer Overview (parent admin)
  learn_coaching_customer_overview: ({ data, loading, error }: WidgetContentProps) => (
    <CustomerCoachingOverviewWidget data={data} loading={loading} error={error ?? undefined} />
  ),
};

// ─── CHART_TYPE_RENDERERS ─────────────────────────────────────────────────────
// Keyed by chartType. Used as final fallback after templateKey and metricKey checks.

export const CHART_TYPE_RENDERERS: Record<string, ComponentType<WidgetContentProps>> = {
  profile_card: ({ data, visualizationConfig }: WidgetContentProps) => (
    <ProfileCard data={data} options={visualizationConfig?.options} />
  ),

  status_summary: ({ data, visualizationConfig }: WidgetContentProps) => (
    <StatusSummary data={data} options={visualizationConfig?.options} />
  ),

  ranked_list: ({ data, visualizationConfig }: WidgetContentProps) => (
    <RankedList data={data} options={visualizationConfig?.options} />
  ),

  action_list: ({ data, visualizationConfig }: WidgetContentProps) => (
    <ActionList data={data} options={visualizationConfig?.options} />
  ),

  multi_kpi: ({ data, visualizationConfig }: WidgetContentProps) => (
    <MultiKPI data={data} options={visualizationConfig?.options} kpis={visualizationConfig?.kpis} />
  ),

  card_list: ({ data, visualizationConfig }: WidgetContentProps) => (
    <CardList data={data} options={visualizationConfig?.options} />
  ),

  goal_list: ({ data, visualizationConfig }: WidgetContentProps) => (
    <GoalList data={data} options={visualizationConfig?.options} />
  ),

  review_cycle_card: ({ data, visualizationConfig }: WidgetContentProps) => (
    <ReviewCycleCard data={data} options={visualizationConfig?.options} />
  ),

  checkin_card: ({ data, visualizationConfig }: WidgetContentProps) => (
    <CheckInCycleCard data={data} options={visualizationConfig?.options} />
  ),

  checkin_cycle_card: ({ data, visualizationConfig }: WidgetContentProps) => (
    <CheckInCycleCard data={data} options={visualizationConfig?.options} />
  ),

  pending_tasks_card: ({ data, visualizationConfig }: WidgetContentProps) => (
    <PendingTasksCard data={data} options={visualizationConfig?.options} />
  ),

  learning_plan_card: ({ data, visualizationConfig }: WidgetContentProps) => (
    <LearningPlanCard data={data} options={visualizationConfig?.options} />
  ),

  cycle_card: ({ data, visualizationConfig }: WidgetContentProps) => (
    <CycleCard data={data} options={visualizationConfig?.options} />
  ),

  at_risk_card: ({ data, visualizationConfig }: WidgetContentProps) => (
    <AtRiskCard data={data} options={visualizationConfig?.options} />
  ),

  goal_card: ({ data, visualizationConfig }: WidgetContentProps) => (
    <GoalCard data={data} options={visualizationConfig?.options} />
  ),

  employee_card: ({ data, visualizationConfig }: WidgetContentProps) => (
    <EmployeeCard data={data} options={visualizationConfig?.options} />
  ),

  stale_goal_card: ({ data, visualizationConfig }: WidgetContentProps) => (
    <StaleGoalCard data={data} options={visualizationConfig?.options} />
  ),

  calendar_card: ({ data, loading, error }: WidgetContentProps) => (
    <CalendarCardWidget data={data} loading={loading} error={error ?? undefined} />
  ),

  goals_progress: ({ data, visualizationConfig }: WidgetContentProps) => (
    <GoalsProgressWidget goals={data} options={visualizationConfig?.options} />
  ),

  pip_card: ({ data, visualizationConfig }: WidgetContentProps) => (
    <PIPsWidget pips={data} options={visualizationConfig?.options} />
  ),

  idp_card: ({ data, visualizationConfig }: WidgetContentProps) => (
    <IDPsWidget idps={data} options={visualizationConfig?.options} />
  ),

  feedback_card: ({ data, visualizationConfig, resolvedMetricKey, queryConfig }: WidgetContentProps) => {
    const key = resolvedMetricKey || queryConfig?.metric_key || '';
    const variant = key.includes('reviewee') ? 'reviewee' : 'reviewer';
    return <FeedbackCard data={data} options={visualizationConfig?.options} variant={variant} />;
  },

  feedback_cards: ({ data, visualizationConfig }: WidgetContentProps) => (
    <FeedbacksReceivedWidget feedbacks={data} options={visualizationConfig?.options} />
  ),

  alert_cards: ({ data, visualizationConfig }: WidgetContentProps) => (
    <AlertCards data={data} options={visualizationConfig?.options} />
  ),

  performance_summary: ({ data, visualizationConfig }: WidgetContentProps) => (
    <PerformanceSummaryWidget data={data} options={visualizationConfig?.options} />
  ),

  dual_bar: ({ data, visualizationConfig }: WidgetContentProps) => (
    <RatingComparisonList
      data={data}
      options={visualizationConfig?.options}
      series={visualizationConfig?.series}
    />
  ),

  grouped_bar: ({ data, visualizationConfig }: WidgetContentProps) => (
    <RatingComparisonList
      data={data}
      options={visualizationConfig?.options}
      series={visualizationConfig?.series}
    />
  ),

  radar: ({ data, visualizationConfig }: WidgetContentProps) => (
    <RatingComparisonList
      data={data}
      options={visualizationConfig?.options}
      series={visualizationConfig?.series}
    />
  ),

  rating_by_review: ({ data, visualizationConfig }: WidgetContentProps) => (
    <RatingComparisonList
      data={data}
      options={visualizationConfig?.options}
      series={visualizationConfig?.series}
    />
  ),

  rating_by_competency: ({ data, visualizationConfig }: WidgetContentProps) => (
    <RatingComparisonList
      data={data}
      options={visualizationConfig?.options}
      series={visualizationConfig?.series}
    />
  ),
};
